/**
 * tutorialBus — how the app tells a running tutorial that something happened.
 *
 * Steps advance on real outcomes ("a start situation now exists", "that edge was
 * created", "the scenario published"), not on the user clicking Next. Those
 * outcomes are emitted from the context actions that produce them, because every
 * entry point — palette drop, mobile tap, keyboard shortcut, copy, delete modal —
 * funnels through the same few functions, and those functions are where the new
 * id is minted.
 *
 * The bus is module-level rather than a context so emitters do not have to be
 * inside the provider, and so a component that emits does not re-render when the
 * tutorial state changes.
 */

const listeners = new Map();
const anyListeners = new Set();

/**
 * @param {string} name
 * @param {(payload: any) => any} fn
 * @returns {() => void} unsubscribe
 */
function on(name, fn) {
    if (!listeners.has(name)) listeners.set(name, new Set());
    listeners.get(name).add(fn);
    return () => {
        const set = listeners.get(name);
        if (!set) return;
        set.delete(fn);
        if (set.size === 0) listeners.delete(name);
    };
}

/**
 * Subscribes to every event. The tutorial provider uses this so it does not have
 * to know up front which events the active tutorial cares about.
 *
 * @param {(name: string, payload: any) => any} fn
 * @returns {() => void} unsubscribe
 */
function onAny(fn) {
    anyListeners.add(fn);
    return () => anyListeners.delete(fn);
}

function collect(name) {
    const named = listeners.get(name);
    return [
        ...(named ? Array.from(named) : []),
        ...Array.from(anyListeners).map((fn) => (payload) => fn(name, payload)),
    ];
}

/**
 * Fire and forget. A throwing listener is logged and cannot stop the others, so
 * a broken tutorial never breaks the editor.
 */
function emit(name, payload) {
    for (const fn of collect(name)) {
        try {
            fn(payload);
        } catch (error) {
            console.error(`tutorialBus listener for "${name}" threw:`, error);
        }
    }
}

/**
 * Like `emit`, but resolves once every listener has settled. Used where the app
 * must wait on what the tutorial does in response — creating the scenario waits
 * for the tutorial to write the description and tag before navigating away.
 */
function emitAsync(name, payload) {
    return Promise.all(
        collect(name).map((fn) =>
            Promise.resolve()
                .then(() => fn(payload))
                .catch((error) => {
                    console.error(`tutorialBus listener for "${name}" rejected:`, error);
                })
        )
    );
}

/** Test helper: drops every subscription. */
function reset() {
    listeners.clear();
    anyListeners.clear();
}

export const tutorialBus = { on, onAny, emit, emitAsync, reset };

export default tutorialBus;
